import React, { useLayoutEffect, useRef } from "react"
import { storyblokEditable } from "gatsby-source-storyblok"
import Background from "../parts/background/background.js"
import ScrollerSection from "../parts/scroller_section/scroller_section.js"
import { gsap } from "gsap"
import { ScrollTrigger } from "gsap/ScrollTrigger"
import { ScrollToPlugin } from "gsap/ScrollToPlugin"
import classNames from "classnames"

const Scroller = ({ blok }) => {
  const main = useRef()

  const registerAnimations = () => {
    const blockHeader = main.current.querySelector(".block-header")

    // Anchors toggle active status and handle click
    const links = gsap.utils.toArray(".block-anchors a")

    function setActive(link) {
      link
        .closest(".block-anchors")
        .querySelectorAll("a")
        .forEach(el => el.classList.remove("active"))
      link.classList.add("active")
    }
    links.forEach(a => {
      const element = document.querySelector(a.getAttribute("href"))
      const linkScrollTrigger = ScrollTrigger.create({
        trigger: element,
        start: "top top",
      })
      const anchors = a.closest('.block-anchors')
      const isDesktop = anchors.classList.contains('block-anchors--desktop');
      ScrollTrigger.create({
        trigger: element,
        start: isDesktop ? "top center" : `top top+=${80 + blockHeader.offsetHeight}px`,
        end: isDesktop ? "bottom center" : `bottom top+=${80 + blockHeader.offsetHeight}px`,
        markers: false,
        onToggle: self => self.isActive && setActive(a),
      })
      a.addEventListener("click", e => {
        e.preventDefault()
        gsap.to(window, {
          duration: 1,
          scrollTo: linkScrollTrigger.start - 160,
          overwrite: "auto",
        })
      })
    })

    // Header sticky
    const blockSections = main.current.querySelector(".block-sections")
    const blockSection = blockSections.querySelector(".scroller-section:last-child")
    ScrollTrigger.create({
      trigger: blockHeader,
      start: "top 80px center",
      end: `+=${blockSections.offsetHeight - blockSection.offsetHeight}px`,
      pin: true,
      // markers: { indent: 25 },
    })

    // Anchors sticky
    const blockNavDesktop = main.current.querySelector(
      ".block-anchors--desktop"
    )
    ScrollTrigger.create({
      trigger: blockNavDesktop,
      start: `center center+=${(80 + blockHeader.offsetHeight - blockNavDesktop.offsetHeight) / 2}`,
      end: `+=${blockSections.offsetHeight - blockSection.offsetHeight}px`,
      pin: true,
      // markers: { indent: 50 },
    })

    // Background sticky
    const blockBg = main.current.querySelector(".background-settings")
    ScrollTrigger.create({
      trigger: blockBg,
      start: "center-=20px center",
      end: `+=${blockSections.offsetHeight - blockSection.offsetHeight}px`,
      pin: true,
      // markers: { indent: 75 },
    })
  }

  useLayoutEffect(() => {
    const ctx = gsap.context(self => {
      gsap.registerPlugin(ScrollTrigger, ScrollToPlugin)
      registerAnimations()
    }, main) // <- Scope!
    return () => ctx.revert() // <- Cleanup!
  }, [])

  const getAnchors = (isDesktop = true) => (
    <nav
      className={`block-anchors ${
        isDesktop ? "block-anchors--desktop" : "block-anchors--tablet"
      }`}
    >
      {blok.scroller_sections.map((blok, index) => (
        <a
          key={blok._uid}
          href={`#${blok.title.toLowerCase()}_${blok._uid}`}
          className={index ? "" : "active"}
        >
          {blok.title}
        </a>
      ))}
    </nav>
  )

  return (
    <section
      className="block_scroller content_block"
      {...storyblokEditable(blok)}
      key={blok._uid}
      ref={main}
    >
      <div className="inner">
        <Background blok={blok.background_settings} />

        <div className="block-header">
          <h2 className="block-title">{blok.section_title}</h2>

          {getAnchors(false)}
        </div>

        {blok.scroller_sections && (
          <div className="block-main">
            {getAnchors(true)}

            <div
              className={classNames("block-sections", {
                "block-sections--wide": blok.support_for_long_text,
              })}
            >
              {blok.scroller_sections.map(blok => (
                <ScrollerSection key={blok._uid} blok={blok} />
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  )
}

export default Scroller
